import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Option, GroupedOption } from '../Types';
import { groupedOptions } from '../utils/Helpers';
import CategoryInfoTooltip from './CategoryInfoTooltip';
import { categoryDefinitions } from './categoryDefinitions';
import '../css/topmenu.scss';
import '../css/crosssegmentmodal.scss';
import Infobox from './InfoBox/InfoBox';

const MAX_SEGMENTS = 4;
const MAX_SUBMISSIONS = MAX_SEGMENTS + 1;

const CrossSegmentMenu: React.FC<{
  onOptionChange: (options: Option[][]) => void;
  crossSegmentSelections: Option[][];
}> = ({ onOptionChange, crossSegmentSelections }) => {
  const [submissions, setSubmissions] = useState<Option[][]>([[]]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSels, setModalSels] = useState<Record<string, Set<string>>>({});

  useEffect(() => {
    setSubmissions(crossSegmentSelections);
  }, [crossSegmentSelections]);

  const atSegmentLimit = submissions.length >= MAX_SUBMISSIONS;

  const openModal = () => {
    if (atSegmentLimit) return;
    setModalSels({});
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (!isModalOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isModalOpen]);

  const toggleOpt = (groupLabel: string, optVal: string) => {
    setModalSels(prev => {
      const cur = prev[groupLabel] ?? new Set<string>();
      const next = new Set(cur);
      if (next.has(optVal)) next.delete(optVal); else next.add(optVal);
      return { ...prev, [groupLabel]: next };
    });
  };

  const setAllForGroup = (groupLabel: string) => {
    const group = groupedOptions.find(g => g.label === groupLabel)!;
    setModalSels(prev => ({ ...prev, [groupLabel]: new Set(group.options.map(o => o.value)) }));
  };

  const setNoneForGroup = (groupLabel: string) => {
    setModalSels(prev => ({ ...prev, [groupLabel]: new Set() }));
  };

  const isChecked = (groupLabel: string, optVal: string) =>
    !!(modalSels[groupLabel]?.has(optVal));

  const isActiveGroup = (group: GroupedOption) =>
    !!(modalSels[group.label]?.size > 0);

  const hasAnySelection = Object.values(modalSels).some(sel => sel.size > 0);

  const buildSegmentOptions = (sels: Record<string, Set<string>>): Option[] => {
    const result: Option[] = [];
    groupedOptions.forEach(group => {
      const sel = sels[group.label];
      if (!sel || sel.size === 0) return;
      group.options.forEach(opt => { if (sel.has(opt.value)) result.push(opt); });
    });
    return result;
  };

  const handleAddSegment = () => {
    if (atSegmentLimit || !hasAnySelection) return;
    const newSegment = buildSegmentOptions(modalSels);
    if (newSegment.length === 0) return;
    const newSubmissions = [...submissions, newSegment];
    setSubmissions(newSubmissions);
    onOptionChange(newSubmissions);
    setModalSels({});
    closeModal();
  };

  return (
    <div className="menu-container">
      <div className="menu-header" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="csm-bar__trigger"
            onClick={openModal}
            disabled={atSegmentLimit}
            title={atSegmentLimit ? `Maximum of ${MAX_SEGMENTS} segments reached` : undefined}
          >
            <FilterIcon />
            Add Segment
            {submissions.length > 1 && (
              <span className="csm-bar__badge">{submissions.length - 1}</span>
            )}
          </button>
        </div>
        <Infobox style={{ display: 'flex', position: 'relative', padding: 12 }}>
          <p>Select attributes to define and add a specific population segment for comparison
          purposes (up to {MAX_SEGMENTS} segments). The default view shows data for 'all'
          individuals aged 15 and older.</p>
        </Infobox>
      </div>

      {isModalOpen && createPortal(
        <div
          className="csm-backdrop"
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="csm-modal" role="dialog" aria-modal="true"
               aria-label="Select segment attributes">
            <div className="csm-modal__header">
              <span className="csm-modal__title">Select segment attributes</span>
              <button className="csm-modal__esc-btn" onClick={closeModal}>ESC</button>
            </div>
            <div className="csm-modal__grid">
              {groupedOptions.map(group => (
                <div
                  key={group.label}
                  className={`csm-dim${isActiveGroup(group) ? ' csm-dim--active' : ''}`}
                >
                  <div className="csm-dim__head">
                    <span className="csm-dim__title">
                      {group.label}
                      {categoryDefinitions[group.label] && (
                        <CategoryInfoTooltip definition={categoryDefinitions[group.label]} />
                      )}
                    </span>
                    <div className="csm-dim__shortcuts">
                      <button onClick={() => setAllForGroup(group.label)}>All</button>
                      <span>·</span>
                      <button onClick={() => setNoneForGroup(group.label)}>None</button>
                    </div>
                  </div>
                  <div className="csm-dim__opts">
                    {group.options.map(opt => (
                      <label key={opt.value} className="csm-opt">
                        <input
                          type="checkbox"
                          checked={isChecked(group.label, opt.value)}
                          onChange={() => toggleOpt(group.label, opt.value)}
                        />
                        <span className="csm-opt__label">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="csm-modal__footer">
              <div className="csm-modal__footer-btns">
                <button className="csm-btn csm-btn--ghost"
                        onClick={() => setModalSels({})}>
                  Reset
                </button>
                <button
                  className="csm-btn csm-btn--primary"
                  onClick={handleAddSegment}
                  disabled={atSegmentLimit || !hasAnySelection}
                >
                  Add Segment
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CrossSegmentMenu;

const FilterIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"
       style={{ marginRight: 5 }}>
    <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z" />
  </svg>
);
